import { useQuery } from "@tanstack/react-query";

import { GetSignalsResponse } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { queryKeys } from "@/constants";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

interface UseGetSignalsArgs {
  userToken: string;
  workspaceId: number;
  promptId?: number;
  q?: string;
  page?: number;
  perPage?: number;
  enabled?: boolean;
}

export const getSignalsQueryKeyPrefix = (workspaceId: number) =>
  [queryKeys.signals, workspaceId] as const;

export const getSignalsQueryKey = ({
  workspaceId,
  promptId,
  q,
  page = 1,
  perPage = 20,
}: {
  workspaceId: number;
  promptId?: number;
  q?: string;
  page?: number;
  perPage?: number;
}) =>
  [
    ...getSignalsQueryKeyPrefix(workspaceId),
    "list",
    promptId ?? null,
    q?.trim() || null,
    page,
    perPage,
  ] as const;

export const useGetSignals = ({
  userToken,
  workspaceId,
  promptId,
  q,
  page = 1,
  perPage = 20,
  enabled = true,
}: UseGetSignalsArgs) => {
  const searchQuery = q?.trim() || undefined;

  return useQuery({
    queryKey: getSignalsQueryKey({ workspaceId, promptId, q: searchQuery, page, perPage }),
    queryFn: async (): Promise<GetSignalsResponse> => {
      const url = new URL(ENDPOINTS.signals.list(workspaceId));
      url.searchParams.set("page", String(page));
      url.searchParams.set("per_page", String(perPage));
      if (promptId !== undefined) {
        url.searchParams.set("prompt_id", String(promptId));
      }
      if (searchQuery) {
        url.searchParams.set("q", searchQuery);
      }

      const response = await fetch(url, {
        headers: jsonAuthHeaders(userToken),
      });

      return handleResponse(response);
    },
    enabled: enabled && Boolean(userToken) && workspaceId > 0,
    keepPreviousData: true,
  });
};
